import { ApiService as API, Hold, Transaction } from './api';

export interface EscrowFlowResult {
  success: boolean;
  holdId: string;
  createHoldSignature: string;
  commitHoldSignature?: string;
  voidHoldSignature?: string;
  slot?: number;
  finalStatus: 'committed' | 'voided' | 'expired' | 'cancelled';
  error?: string;
}

export class EscrowService {
  // private static readonly VALIDATION_TIMEOUT = 2 * 60 * 1000; // 2 minutes
  private static readonly POLL_INTERVAL = 2000; // 2 seconds

  /**
   * Simulates third-party validation (KYC, compliance, etc.)
   * For now, always returns true, but can be extended with real validation logic
   */
  private static async performValidation(holdId: string): Promise<boolean> {
    // Simulate validation delay (1-3 seconds)
    const delay = Math.random() * 2000 + 1000;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // For now, always return true (validation passed)
    // In real implementation, this would call external APIs
    console.log(`Validation completed for hold ${holdId}: PASSED`);
    return true;
  }

  /**
   * Monitors hold status and handles automatic expiry
   */
  private static async monitorHoldStatus(
    holdId: string,
    onStatusUpdate: (status: string, transaction: Transaction | undefined) => void,
    onExpiry: (transaction: Transaction | undefined) => void
  ): Promise<void> {
    // const startTime = Date.now();
    
    const checkStatus = async () => {
      try {
        console.log('Checking hold status');
        const hold = await API.getHoldById(holdId);
        console.log('Hold details:', hold);
        let transaction: Transaction | undefined;
        if(hold.status === 'committed' || hold.status === 'voided') {
          console.log('Hold is committed or voided, getting transaction');
          transaction = await API.getTransactionByHoldId(hold.holdId);
          console.log('Got Transaction:', transaction);
        }
        console.log('Updating status:', hold.status);
        await onStatusUpdate(hold.status, transaction);
        console.log('Updated status:', hold.status);
        // Check if hold has expired
        const now = Date.now();
        const expiresAt = new Date(hold.expiresAt).getTime();

        if (now > expiresAt && hold.status === 'voided') {
          onExpiry(transaction);
          return;
        }
        
        // Continue monitoring if still pending and not expired
        if (hold.status === 'pending') {
          setTimeout(checkStatus, this.POLL_INTERVAL);
        }
      } catch (error) {
        console.error('Error monitoring hold status:', error);
        setTimeout(checkStatus, this.POLL_INTERVAL);
      }
    };
    
    await checkStatus();
  }

  /**
   * Main escrow flow: create hold -> validate -> commit/void
   */
  static async processEscrowFlow(
    receiverCompanyWallet: string,
    amount: number,
    wallet: any,
    onStatusUpdate: (status: string, message: string) => void
  ): Promise<EscrowFlowResult> {
    try {
      // Step 1: Create hold
      onStatusUpdate('CREATING_HOLD', 'Creating escrow hold...');
      const hold = await API.createHold(
        { receiverCompanyWallet, amount: amount * 100 },
        wallet
      );
      
      onStatusUpdate('HOLD_CREATED', 'Hold created successfully');
      
      // Step 2: Start monitoring hold status
      let finalStatus: 'committed' | 'voided' | 'expired' = 'voided';
      let commitHoldSignature: string | undefined;
      let voidHoldSignature: string | undefined;
      let slot: number | undefined;
      
      const statusPromise = new Promise<void>((resolve) => {
        this.monitorHoldStatus(
          hold.holdId,
          (status, transaction) => {
            if (status === 'committed') {
              finalStatus = 'committed';
              commitHoldSignature = transaction?.solanaSignature;
              slot = transaction?.slot;
              resolve();
            } else if (status === 'voided') {
              finalStatus = 'voided';
              voidHoldSignature = transaction?.solanaSignature;
              slot = transaction?.slot;
              resolve();
            }
          },
          (transaction) => {
            finalStatus = 'expired';
            voidHoldSignature = transaction?.solanaSignature;
            slot = transaction?.slot;
            resolve();
          }
        );
      });

      // Step 3: Perform validation
      onStatusUpdate('VALIDATING', 'Performing validation checks...');
      const validationResult = await this.performValidation(hold.holdId);
      
      if (validationResult) {
        onStatusUpdate('VALIDATION_PASSED', 'Validation successful');
        onStatusUpdate('COMMITTING', 'Committing hold...');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        // commitHoldSignature = 'simulated_commit_signature';
      } else {
        onStatusUpdate('VALIDATION_FAILED', 'Validation failed');
        onStatusUpdate('VOIDING', 'Voiding hold...');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        // voidHoldSignature = 'simulated_void_signature';
      }

      // Wait for final status
      await statusPromise;
      
      onStatusUpdate('COMPLETED', `Hold ${finalStatus.toLowerCase()}`);
      
      return {
        success: true,
        holdId: hold.holdId,
        createHoldSignature: hold.solanaSignature || '',
        commitHoldSignature,
        voidHoldSignature,
        slot,
        finalStatus
      };
      
    } catch (error) {
      console.error('Escrow flow error:', error);
      onStatusUpdate('ERROR', 'Escrow flow failed');
      
      return {
        success: false,
        holdId: '',
        createHoldSignature: '',
        finalStatus: 'cancelled',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get hold details by ID
   */
  static async getHoldDetails(holdId: string): Promise<Hold> {
    return API.getHoldById(holdId);
  }

  /**
   * Get holds for a company
   */
  static async getCompanyHolds(companyWallet: string, status?: string): Promise<Hold[]> {
    return API.getHoldsByCompany(companyWallet, status);
  }

  /**
   * Get treasury information
   */
  static async getTreasuryInfo() {
    return API.getTreasury();
  }
}
